import { authAtom } from "@src/atoms";
import LoadingSpinner from "@src/components/loading-spinner";
import { useSubscription } from "@src/context/SubscriptionContext";
import { useUser } from "@src/context/UserContext";
import { Role } from "@src/gql/generated";
import { isAiChatRoute } from "@src/utils/ai-chat-routes";
import { checkAuth } from "@src/utils/helpers/check-auth";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

function PrivateRoute() {
  const [auth, setAuth] = useAtom(authAtom);
  const [isChecking, setIsChecking] = useState(false);
  const location = useLocation();
  const { user: contextUser, loading: userLoading } = useUser();
  const { hasActiveSubscription, loading: subscriptionLoading } =
    useSubscription();

  useEffect(() => {
    const validateAuth = async () => {
      if (auth.isAuthenticated === null && !isChecking) {
        setIsChecking(true);

        try {
          const result = await checkAuth();
          setAuth({
            isAuthenticated: result.isAuthenticated,
            user: result.user,
          });
        } catch (error) {
          console.error("Auth check failed:", error);
          setAuth({ isAuthenticated: false, user: null });
        } finally {
          setIsChecking(false);
        }
      }
    };

    validateAuth();
  }, [auth.isAuthenticated, isChecking, setAuth]);

  if (auth.isAuthenticated === null || isChecking) {
    return (
      <div className="w-screen h-screen flex items-center justify-center">
        <LoadingSpinner height={100} width={100} />
      </div>
    );
  }

  if (auth.isAuthenticated === false) {
    // Use the router location: window.location may already be /sign-in when
    // this re-renders mid-redirect, which would send the user back here after login.
    const intendedPath = location.pathname + location.search;
    localStorage.setItem("postLoginRedirect", intendedPath);
    return <Navigate to="/sign-in" replace />;
  }

  const userRole = auth.user?.role ?? contextUser?.role;
  const isIndustryPartner = userRole === Role.IndustryPartners;
  const isPlansRoute = location.pathname.startsWith("/plans");
  const isContractConsultantRoute = isAiChatRoute(location.pathname);
  // Wait for /me before rendering protected routes — prevents feed → spinner → feed
  const rolePending = !userRole && userLoading;

  if (rolePending) {
    return (
      <div className="w-screen h-screen flex items-center justify-center">
        <LoadingSpinner height={100} width={100} />
      </div>
    );
  }

  if (isIndustryPartner && !isPlansRoute) {
    if (subscriptionLoading) {
      return (
        <div className="w-screen h-screen flex items-center justify-center">
          <LoadingSpinner height={100} width={100} />
        </div>
      );
    }

    if (!hasActiveSubscription && !isContractConsultantRoute) {
      return <Navigate to="/plans" replace />;
    }
  }

  return <Outlet />;
}

export default PrivateRoute;
